import React, { useState, useEffect } from 'react';
import { useOrganization } from '../../hooks/useOrganization';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { Users, Mail, Plus, Copy, Check, Crown, Shield, User, Trash2, LayoutGrid, ChevronUp, ChevronDown, X, Pencil, LogOut, ChevronDown as ChevronDownIcon, MoreVertical, Gauge } from 'lucide-react';
import { useKanbanColumns, COLOR_STYLES, COLOR_DOTS } from '../../hooks/useKanbanColumns';
import { useNotifications } from '../../context/NotificationContext';
import { useQuotas } from '../../hooks/useQuotas';
import { PLANS } from '../../lib/plans';
import QuotaMeter from '../shared/QuotaMeter';

// Petit menu d'actions inline pour chaque membre
function MemberActions({
  member,
  currentUserId,
  currentRole,
  onRemove,
  onChangeRole,
}: {
  member: Member;
  currentUserId: string;
  currentRole: string;
  onRemove: (m: Member) => void;
  onChangeRole: (m: Member, role: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isSelf = member.user_id === currentUserId;
  const canManage = (currentRole === 'owner' || currentRole === 'admin') && member.role !== 'owner';
  if (!canManage) return null;

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
        title="Actions"
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[170px]">
            {member.role !== 'admin' && (
              <button
                onClick={() => { onChangeRole(member, 'admin'); setOpen(false); }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Shield size={13} className="text-blue-400" /> Promouvoir Admin
              </button>
            )}
            {member.role !== 'member' && (
              <button
                onClick={() => { onChangeRole(member, 'member'); setOpen(false); }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <User size={13} className="text-slate-400" /> Rétrograder Membre
              </button>
            )}
            <div className="my-1 border-t border-slate-100" />
            <button
              onClick={() => { onRemove(member); setOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:bg-red-50"
            >
              <LogOut size={13} /> {isSelf ? "Quitter l'org" : 'Retirer du projet'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface Member {
  user_id: string;
  email: string;
  full_name?: string;
  role: string;
  joined_at: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  token: string;
}

export default function SettingsView() {
  const { org, loading: orgLoading } = useOrganization();
  const { user } = useAuth();
  const { notify } = useNotifications();
  const { plan, items: quotaItems, loading: quotasLoading } = useQuotas();
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState<Member | null>(null);
  const [confirmInvite, setConfirmInvite] = useState<{ email: string; orgs: string[] } | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [inviting, setInviting] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  // Kanban columns
  const { columns, isSaving, addColumn, updateColumn, deleteColumn, moveColumn } = useKanbanColumns();
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftEmoji, setDraftEmoji] = useState('');
  const [draftColor, setDraftColor] = useState('slate');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newEmoji, setNewEmoji] = useState('');
  const [newColor, setNewColor] = useState('slate');

  const saveColumnEdit = async () => {
    if (!editingSlug || !draftLabel.trim()) return;
    await updateColumn(editingSlug, { label: draftLabel.trim(), emoji: draftEmoji, color: draftColor });
    setEditingSlug(null);
  };

  const handleAddColumn = async () => {
    if (!newLabel.trim()) return;
    await addColumn(newLabel.trim(), newEmoji || '🔵', newColor);
    setNewLabel('');
    setNewEmoji('');
    setNewColor('slate');
    setShowAddForm(false);
    notify('success', 'Colonne ajoutée');
  };

  const handleDeleteColumn = async (slug: string) => {
    const result = await deleteColumn(slug);
    if (result.blocked) {
      notify('error', `Impossible de supprimer la colonne`, { description: `${result.count} mission(s) dans cette colonne` });
    } else {
      notify('success', 'Colonne supprimée');
    }
  };

  useEffect(() => {
    if (!org) return;
    loadMembers();
    loadInvites();
  }, [org?.id]);

  const loadMembers = async () => {
    setLoadingMembers(true);
    const { data } = await supabase.rpc('get_org_members');
    if (data) setMembers(data);
    setLoadingMembers(false);
  };

  const loadInvites = async () => {
    const { data } = await supabase
      .from('organization_invites')
      .select('id, email, role, expires_at, token')
      .is('used_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    if (data) setInvites(data);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org) return;
    const email = inviteEmail.trim();
    if (!email) return;

    setInviting(true);
    const { data, error } = await supabase.rpc('get_invite_email_status', {
      p_email: email,
      p_org_id: org.id,
    });
    const status = data as { has_account: boolean; orgs: string[]; already_member: boolean } | null;

    // En cas d'erreur RPC on n'empêche pas d'inviter : la confirmation est un garde-fou,
    // pas une condition.
    if (!error && status?.already_member) {
      setInviting(false);
      notify('error', 'Déjà membre', {
        description: `${email} fait déjà partie de ${org.name}.`,
      });
      return;
    }

    if (!error && status?.has_account && status.orgs.length > 0) {
      setInviting(false);
      setConfirmInvite({ email, orgs: status.orgs });
      return;
    }

    await createInvite(email);
  };

  const createInvite = async (invitedEmail: string) => {
    if (!org) return;
    setConfirmInvite(null);
    setInviting(true);
    const token = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const { error } = await supabase.from('organization_invites').insert({
      org_id: org.id,
      email: invitedEmail,
      role: inviteRole,
      token,
      expires_at: expiresAt.toISOString(),
    });

    if (error) {
      notify('error', "Erreur lors de la création de l'invitation");
    } else {
      const link = `${window.location.origin}/invite/${token}`;
      const session = (await supabase.auth.getSession()).data.session;
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-invite', {
        body: { email: invitedEmail, redirectTo: link, orgName: org.name },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      setGeneratedLink(link);
      setInviteEmail('');
      loadInvites();
      if (sendError) {
        notify('error', "Invitation créée, mais l'email n'a pas pu être envoyé", {
          description: `Partagez le lien d'invitation manuellement à ${invitedEmail}.`,
        });
      } else if (sendResult?.method === 'brevo') {
        // Le mail porte notre lien /invite/<token>, qui ne s'use pas : il reste
        // valable même si la messagerie du destinataire l'ouvre avant lui.
        notify('success', 'Invitation envoyée', {
          description: `${invitedEmail} a reçu son lien d'invitation. Il reste valable 7 jours.`,
        });
      } else if (sendResult?.method === 'magiclink') {
        notify('success', 'Invitation créée', {
          description: `${invitedEmail} a déjà un compte : un email de connexion menant à l'invitation lui a été envoyé.`,
        });
      } else {
        notify('success', 'Invitation créée', { description: 'Email envoyé à ' + invitedEmail });
      }
    }
    setInviting(false);
  };

  const copyLink = async (link: string) => {
    await navigator.clipboard.writeText(link);
    setCopiedLink(link);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const revokeInvite = async (id: string) => {
    await supabase.from('organization_invites').delete().eq('id', id);
    loadInvites();
    notify('info', 'Invitation révoquée');
  };

  const handleRemoveMember = async (member: Member) => {
    if (!org) return;
    const { error } = await supabase
      .from('organization_members')
      .delete()
      .eq('org_id', org.id)
      .eq('user_id', member.user_id);
    if (error) {
      notify('error', 'Erreur lors du retrait du membre');
    } else {
      notify('success', `Membre retiré`, { description: `${member.email} n'a plus accès à l'organisation.` });
      setConfirmRemove(null);
      loadMembers();
    }
  };

  const handleChangeRole = async (member: Member, newRole: string) => {
    if (!org) return;
    const { error } = await supabase
      .from('organization_members')
      .update({ role: newRole })
      .eq('org_id', org.id)
      .eq('user_id', member.user_id);
    if (error) {
      notify('error', 'Erreur lors du changement de rôle');
    } else {
      const label = newRole === 'admin' ? 'Admin' : 'Membre';
      notify('success', `Rôle mis à jour`, { description: `${member.email} est maintenant ${label}.` });
      loadMembers();
    }
  };

  const currentUserRole = org?.role ?? 'member';

  const roleIcon = (role: string) => {
    if (role === 'owner') return <Crown size={14} className="text-amber-500" />;
    if (role === 'admin') return <Shield size={14} className="text-blue-400" />;
    return <User size={14} className="text-slate-400" />;
  };

  const roleLabel = (role: string) => {
    if (role === 'owner') return 'Propriétaire';
    if (role === 'admin') return 'Admin';
    return 'Membre';
  };

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  const canInvite = org?.role === 'owner' || org?.role === 'admin';

  return (
    <>
    {/* Modale de confirmation d'offboarding */}
    {confirmRemove && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 w-full max-w-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <LogOut size={18} className="text-red-500" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">
                {confirmRemove.user_id === user?.id ? "Quitter l'organisation" : 'Retirer ce membre'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5 break-all">{confirmRemove.email}</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            {confirmRemove.user_id === user?.id
              ? "Vous allez quitter l'organisation. Vous perdrez l'accès à toutes les données."
              : `${confirmRemove.email} perdra l'accès à toutes les données de l'organisation.`}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmRemove(null)}
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => handleRemoveMember(confirmRemove)}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
            >
              {confirmRemove.user_id === user?.id ? 'Quitter' : 'Retirer'}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* Confirmation : l'invité appartient déjà à une organisation */}
    {confirmInvite && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 w-full max-w-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <Users size={18} className="text-amber-500" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">Déjà membre d'une organisation</h3>
              <p className="text-xs text-slate-500 mt-0.5 break-all">{confirmInvite.email}</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            Cette adresse appartient déjà à{' '}
            <strong className="text-slate-800">{confirmInvite.orgs.join(', ')}</strong>. Un email de
            connexion sera envoyé pour rejoindre{' '}
            <strong className="text-slate-800">{org?.name}</strong>. Voulez-vous vraiment l'inviter ?
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmInvite(null)}
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => createInvite(confirmInvite.email)}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors"
            >
              Inviter quand même
            </button>
          </div>
        </div>
      </div>
    )}
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Paramètres</h1>
        <p className="text-slate-500 mt-1">
          Organisation · <span className="font-medium text-slate-700">{org?.name ?? '—'}</span>
        </p>
      </div>

      {/* Forfait & quotas */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <Gauge size={18} className="text-slate-600" />
          <h2 className="font-semibold text-slate-800">Forfait & quotas</h2>
          <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wide">
            {PLANS[plan].label}
          </span>
        </div>
        <p className="text-sm text-slate-400 mb-5">
          Pendant la bêta, le forfait et les quotas sont personnels — chaque membre a les siens. Période en cours :{' '}
          {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} · les compteurs mensuels se
          remettent à zéro le{' '}
          {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('fr-FR')}. Le vivier de
          candidats n'est pas mensuel : il se libère en supprimant des profils.
        </p>
        {quotasLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4">
            {quotaItems.map(item => (
              <QuotaMeter key={item.key} label={item.label} used={item.used} limit={item.limit} />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Members list */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <Users size={18} className="text-slate-600" />
            <h2 className="font-semibold text-slate-800">Membres</h2>
            <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
              {members.length}
            </span>
          </div>

          {loadingMembers ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Aucun membre trouvé</p>
          ) : (
            <div className="space-y-2">
              {members.map(m => (
                <div key={m.user_id} className={`flex items-center gap-3 p-3 rounded-lg group ${m.user_id === user?.id ? 'bg-amber-50 border border-amber-100 hover:bg-amber-50' : 'hover:bg-slate-50'}`}>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {m.email.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-slate-700 truncate">{m.email}</p>
                      {m.user_id === user?.id && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium shrink-0">Vous</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">
                      Depuis le {new Date(m.joined_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 shrink-0">
                    {roleIcon(m.role)}
                    <span>{roleLabel(m.role)}</span>
                  </div>
                  <MemberActions
                    member={m}
                    currentUserId={user?.id ?? ''}
                    currentRole={currentUserRole}
                    onRemove={setConfirmRemove}
                    onChangeRole={handleChangeRole}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invite form — owners and admins only */}
        {canInvite ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <Mail size={18} className="text-slate-600" />
              <h2 className="font-semibold text-slate-800">Inviter un collègue</h2>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="collegue@entreprise.com"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Rôle</label>
                <select
                  value={inviteRole}
                  onChange={e => setInviteRole(e.target.value as 'member' | 'admin')}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                >
                  <option value="member">Membre</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={inviting}
                className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
              >
                <Plus size={16} />
                {inviting ? 'Création...' : "Générer un lien d'invitation"}
              </button>
            </form>

            {generatedLink && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700 font-medium mb-2">Lien à envoyer (valable 7 jours) :</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs text-amber-800 break-all">{generatedLink}</code>
                  <button
                    onClick={() => copyLink(generatedLink)}
                    className="shrink-0 p-1.5 rounded-md hover:bg-amber-100 text-amber-700 transition-colors"
                    title="Copier"
                  >
                    {copiedLink === generatedLink ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            )}

            {invites.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-medium text-slate-500 mb-2">
                  Invitations en attente ({invites.length})
                </p>
                <div className="space-y-2">
                  {invites.map(inv => (
                    <div key={inv.id} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700 truncate">{inv.email}</p>
                        <p className="text-xs text-slate-400">
                          {roleLabel(inv.role)} · expire le{' '}
                          {new Date(inv.expires_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <button
                        onClick={() => copyLink(`${window.location.origin}/invite/${inv.token}`)}
                        className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 transition-colors"
                        title="Copier le lien"
                      >
                        {copiedLink === `${window.location.origin}/invite/${inv.token}`
                          ? <Check size={13} />
                          : <Copy size={13} />
                        }
                      </button>
                      <button
                        onClick={() => revokeInvite(inv.id)}
                        className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        title="Révoquer"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 flex items-center justify-center">
            <p className="text-sm text-slate-400 text-center">
              Seuls les owners et admins peuvent inviter des membres.
            </p>
          </div>
        )}
      </div>

      {/* Kanban Columns */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <LayoutGrid size={18} className="text-slate-600" />
          <h2 className="font-semibold text-slate-800">Colonnes Kanban</h2>
          {isSaving && <span className="text-xs text-slate-400 animate-pulse ml-auto">Sauvegarde…</span>}
        </div>
        <p className="text-sm text-slate-400 mb-5">
          Personnalisez les colonnes de votre kanban missions. Le slug (identifiant interne) est défini à la création et ne peut pas être modifié.
        </p>

        <div className="space-y-2 mb-5">
          {columns.map((col, idx) => (
            <div key={col.slug} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 group">
              {/* Up / Down */}
              <div className="flex flex-col gap-0.5 shrink-0">
                <button
                  onClick={() => moveColumn(col.slug, 'up')}
                  disabled={idx === 0}
                  className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-25 disabled:cursor-not-allowed text-slate-500"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  onClick={() => moveColumn(col.slug, 'down')}
                  disabled={idx === columns.length - 1}
                  className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-25 disabled:cursor-not-allowed text-slate-500"
                >
                  <ChevronDown size={12} />
                </button>
              </div>

              {/* Color dot */}
              <div className={`w-3 h-3 rounded-full shrink-0 ${COLOR_DOTS[col.color] ?? 'bg-slate-400'}`} />

              {editingSlug === col.slug ? (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <input
                    value={draftEmoji}
                    onChange={e => setDraftEmoji(e.target.value)}
                    className="w-12 border border-slate-200 rounded px-1.5 py-1 text-sm text-center shrink-0"
                    maxLength={2}
                  />
                  <input
                    value={draftLabel}
                    onChange={e => setDraftLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveColumnEdit(); if (e.key === 'Escape') setEditingSlug(null); }}
                    className="flex-1 min-w-0 border border-slate-200 rounded px-2 py-1 text-sm"
                    autoFocus
                  />
                  <select
                    value={draftColor}
                    onChange={e => setDraftColor(e.target.value)}
                    className="border border-slate-200 rounded px-2 py-1 text-xs shrink-0"
                  >
                    {Object.keys(COLOR_STYLES).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={saveColumnEdit} className="p-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 shrink-0">
                    <Check size={12} />
                  </button>
                  <button onClick={() => setEditingSlug(null)} className="p-1.5 text-slate-400 hover:text-slate-600 shrink-0">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <>
                  <span className="text-base">{col.emoji}</span>
                  <span className="flex-1 text-sm font-medium text-slate-700">{col.label}</span>
                  <span className="text-xs text-slate-400 font-mono">{col.slug}</span>
                  <button
                    onClick={() => { setEditingSlug(col.slug); setDraftLabel(col.label); setDraftEmoji(col.emoji); setDraftColor(col.color); }}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-slate-200 text-slate-500 transition-opacity"
                    title="Modifier"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => handleDeleteColumn(col.slug)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-opacity"
                    title="Supprimer"
                  >
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {showAddForm ? (
          <div className="flex items-center gap-2 p-3 rounded-xl border border-indigo-100 bg-indigo-50/30">
            <input
              value={newEmoji}
              onChange={e => setNewEmoji(e.target.value)}
              placeholder="🆕"
              className="w-12 border border-slate-200 rounded px-1.5 py-1.5 text-sm text-center shrink-0"
              maxLength={2}
            />
            <input
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="Nom de la colonne"
              onKeyDown={e => { if (e.key === 'Enter') handleAddColumn(); if (e.key === 'Escape') setShowAddForm(false); }}
              className="flex-1 border border-slate-200 rounded px-2 py-1.5 text-sm"
              autoFocus
            />
            <select
              value={newColor}
              onChange={e => setNewColor(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1.5 text-xs shrink-0"
            >
              {Object.keys(COLOR_STYLES).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              onClick={handleAddColumn}
              disabled={!newLabel.trim()}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 disabled:opacity-50 shrink-0"
            >
              <Plus size={13} /> Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="text-slate-400 hover:text-slate-600 shrink-0">
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <Plus size={16} /> Nouvelle colonne
          </button>
        )}
      </div>
    </div>
    </>
  );
}
